import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import { controlResetClassName, cx } from '../../ui/classNames';

// MUI's small outlined Chip in the app theme: a 24px pill (22px compact)
// with a dark line border on the subtle surface, muted ink, 9px semibold
// uppercase text; the label reopens the filter and the × clears it.
const CHIP_CLASS_NAME = cx(
    'box-border inline-flex h-6 max-w-[220px] shrink-0 items-center rounded-full border border-solid',
    'border-ui-line-strong bg-tbl-stripe align-middle text-[9px] font-semibold uppercase leading-[1.5] tracking-normal',
    'text-ui-muted [[data-compact]_&]:h-[22px]',
);
const LABEL_CLASS_NAME = cx(
    controlResetClassName,
    'h-full min-w-0 cursor-pointer truncate rounded-full border-0 bg-transparent px-[7px] text-inherit',
    'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-ui-focus',
);
const CLEAR_CLASS_NAME = cx(
    controlResetClassName,
    '-ml-1 mr-[3px] inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full border-0',
    'bg-transparent p-0 text-accent/70 hover:text-accent focus:outline-none focus-visible:outline',
    'focus-visible:outline-2 focus-visible:outline-ui-focus',
);
// Narrower padding than the kit's control size.
const CLEAR_ALL_CLASS_NAME = '!min-w-0 !px-2';

/**
 * Toolbar chips for the active column filters, such as "Theme: AI, Energy".
 * A chip's label reopens its filter (anchored to the chip) and its × clears
 * it; with more than one filter a single "Clear filters" clears them all.
 */
export default function CategoryFilterChips({ items, onOpen, onClear, onClearAll }) {
    if (!items.length) return null;
    return (
        <div className="flex min-w-0 flex-wrap items-center gap-1" role="group" aria-label="Column filters">
            {items.map((item) => {
                const text = `${item.label}: ${item.summary}`;
                return (
                    <span key={item.key} className={CHIP_CLASS_NAME} title={text}>
                        <button
                            type="button"
                            className={LABEL_CLASS_NAME}
                            onClick={(event) => onOpen(item.key, event.currentTarget.parentElement)}
                        >
                            {text}
                        </button>
                        <button
                            type="button"
                            aria-label={`Clear ${item.label} filter`}
                            title={`Clear ${item.label} filter`}
                            className={CLEAR_CLASS_NAME}
                            onClick={() => onClear(item.key)}
                        >
                            <X aria-hidden="true" size={16} />
                        </button>
                    </span>
                );
            })}
            {items.length > 1 && (
                <Button className={CLEAR_ALL_CLASS_NAME} onClick={onClearAll}>
                    Clear filters
                </Button>
            )}
        </div>
    );
}

CategoryFilterChips.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        summary: PropTypes.string.isRequired,
    })).isRequired,
    onOpen: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onClearAll: PropTypes.func.isRequired,
};
